/**
 * Internal dependencies
 */
import { getClosest } from '../../utils/helpers';
import { removeQueryArgs } from '../../utils/url';
import { debounce } from '../../utils/lodash';
import type { Attributes as ClickAttributes } from '../../../../../packages/conversion-action-library/click/types';
import type { Attributes as ClickExternalLinkAttributes } from '../../../../../packages/conversion-action-library/click-external-link/types';
import type { ConvertibleAction, Convert } from '../../types';

const MILLISECONDS_IN_SECOND = 1000;

export function listenToClickEvent(
	allActions: ReadonlyArray< ConvertibleAction >,
	convert: Convert
): void {
	const clickActions = allActions.filter( isClickAction );
	const externalClickActions = allActions.filter( isClickExternalLinkAction );
	if ( ! clickActions.length && ! externalClickActions ) {
		return;
	}

	const startTime = Date.now();
	const isOldEnough = ( {
		attributes: { delay = 0 },
	}: ConvertibleAction< { delay?: number } > ) =>
		Date.now() - startTime >= delay * MILLISECONDS_IN_SECOND;

	const onClick = debounce(
		( ev: MouseEvent ) => {
			const target = ev.target as HTMLElement | null;
			if ( ! target ) {
				return;
			}

			const anchorTag = getClosest< HTMLAnchorElement >( target, 'a' );

			convert( [
				...clickActions.filter( isOldEnough ).filter( ( action ) => {
					const els = getElements( action.attributes );
					return isExactlyOrDescendant( els, target );
				} ),
				...externalClickActions
					.filter( isOldEnough )
					.filter( doesExternalActionMatch( anchorTag ) ),
			] );
		},
		100,
		{ leading: true, trailing: false }
	);

	document.addEventListener( 'click', onClick );
	document.addEventListener( 'click', onClick, true );
}

// =======
// HELPERS
// =======

function isClickAction(
	action: ConvertibleAction
): action is ConvertibleAction< ClickAttributes > {
	return 'nab/click' === action.type;
}

function isClickExternalLinkAction(
	action: ConvertibleAction
): action is ConvertibleAction< ClickExternalLinkAttributes > {
	return 'nab/click-external-link' === action.type;
}

function isExactlyOrDescendant(
	candidates: ReadonlyArray< Element >,
	el: Element
): boolean {
	return (
		candidates.includes( el ) ||
		candidates.some( ( p ) => p.contains( el ) )
	);
}

function getElements( {
	mode,
	value,
}: ClickAttributes ): ReadonlyArray< Element > {
	try {
		switch ( mode ) {
			case 'id': {
				const el = document.getElementById( value );
				return el ? [ el ] : [];
			}

			case 'class':
				return Array.from( document.getElementsByClassName( value ) );

			case 'css':
				return Array.from( document.querySelectorAll( value ) );
		}
	} catch ( e ) {
		// eslint-disable-next-line no-console
		console.error( e );
		return [];
	}
}

function doesExternalActionMatch( anchorTag: HTMLAnchorElement | false ) {
	if ( ! anchorTag ) {
		return () => false;
	}

	return (
		action: ConvertibleAction< ClickExternalLinkAttributes >
	): boolean => {
		const {
			attributes: { mode, value },
		} = action;
		const href = removeQueryArgs(
			anchorTag.getAttribute( 'href' ) ?? '',
			'nab'
		);

		switch ( mode ) {
			case 'exact':
				return href === value;

			case 'partial':
				return 0 <= href.indexOf( value );

			case 'start':
				return href.startsWith( value );

			case 'end':
				return href.endsWith( value );

			case 'regex':
				try {
					return new RegExp( value ).test( href );
				} catch ( _ ) {
					return false;
				}
		}
	};
}
